// ===========================================
// POSITION WEEK GRID — the 7shifts-style view
// ===========================================
// Rows are people grouped by the position they're scheduled AS (bartenders
// together, servers together…), columns are the week's days, cells are their
// shifts. Answers the two questions the day view can't: "who are my bartenders
// this week?" and "what does Henry's week look like?" — an operator's exact ask.

import { Fragment, useMemo } from 'react';
import clsx from 'clsx';
import type { ShiftInstance } from '../../services/api';
import { parseLocalDate } from '../../hooks/useScheduleBuilder';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface PositionWeekGridProps {
  selectedWeek: { start: string; end: string };
  shiftsByDate: Record<string, ShiftInstance[]>;
  onShiftClick?: (shift: ShiftInstance) => void;
}

const t = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(' ', '').toLowerCase();
const roleLabel = (r: string) => r.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function PositionWeekGrid({ selectedWeek, shiftsByDate, onShiftClick }: PositionWeekGridProps) {
  const weekDates = useMemo(() => {
    const dates: string[] = [];
    const start = parseLocalDate(selectedWeek.start);
    for (let i = 0; i < 7; i++) {
      const d = new Date(start); d.setDate(start.getDate() + i);
      dates.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    }
    return dates;
  }, [selectedWeek.start]);

  // role → employee → date → cells. Grouped by the role they're SCHEDULED as,
  // so someone covering bar on Tuesday shows under Bartenders that day.
  const groups = useMemo(() => {
    type Cell = { shift: ShiftInstance; start: string; end: string };
    const byRole = new Map<string, Map<string, { name: string; hours: number; days: Record<string, Cell[]> }>>();
    for (const date of weekDates) {
      for (const shift of shiftsByDate[date] || []) {
        for (const a of shift.assignments || []) {
          const role = a.role || a.employee?.primaryRole || 'other';
          const empId = a.employeeId;
          const name = a.employee ? `${a.employee.firstName} ${a.employee.lastName}`.trim() : 'Unassigned';
          const roleMap = byRole.get(role) || new Map();
          const row = roleMap.get(empId) || { name, hours: 0, days: {} };
          (row.days[date] = row.days[date] || []).push({ shift, start: a.scheduledStart, end: a.scheduledEnd });
          row.hours += parseFloat(String(a.scheduledHours)) || 0;
          roleMap.set(empId, row);
          byRole.set(role, roleMap);
        }
      }
    }
    return [...byRole.entries()]
      .sort((x, y) => x[0].localeCompare(y[0]))
      .map(([role, emps]) => ({
        role,
        rows: [...emps.values()].sort((x, y) => x.name.localeCompare(y.name)),
      }));
  }, [weekDates, shiftsByDate]);

  const todayStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();

  if (groups.length === 0) {
    return <div className="flex-1 flex items-center justify-center text-sm text-slate-400 p-8">No one is scheduled this week yet — assign people in the day view and they'll appear here by position.</div>;
  }

  return (
    <div className="flex-1 overflow-auto">
      <table className="w-full text-sm border-collapse min-w-[720px]">
        <thead className="sticky top-0 bg-white z-10">
          <tr className="text-xs text-slate-400 border-b border-slate-200">
            <th className="text-left font-semibold px-3 py-2 w-44">Position / person</th>
            {weekDates.map((d) => {
              const date = parseLocalDate(d);
              return (
                <th key={d} className={clsx('font-semibold px-2 py-2 text-center', d === todayStr && 'text-indigo-600')}>
                  {DAY_NAMES[date.getDay()]} {date.getDate()}
                </th>
              );
            })}
            <th className="text-right font-semibold px-3 py-2 w-16">Hrs</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <Fragment key={g.role}>
              <tr className="bg-slate-50/80">
                <td colSpan={9} className="px-3 py-1.5 text-xs font-bold text-slate-500 uppercase tracking-wide">
                  {roleLabel(g.role)} <span className="font-normal text-slate-400">· {g.rows.length}</span>
                </td>
              </tr>
              {g.rows.map((r) => (
                <tr key={g.role + r.name} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">{r.name}</td>
                  {weekDates.map((d) => (
                    <td key={d} className={clsx('px-1.5 py-1.5 align-top', d === todayStr && 'bg-indigo-50/40')}>
                      <div className="space-y-1">
                        {(r.days[d] || []).map((c, i) => (
                          <button key={i} onClick={() => onShiftClick?.(c.shift)}
                            className="block w-full text-center text-[11px] font-medium rounded-lg px-1.5 py-1 transition hover:opacity-80"
                            style={{ backgroundColor: (c.shift.color || '#6366f1') + '1a', color: c.shift.color || '#6366f1' }}
                            title={`${c.shift.name} · ${t(c.start)}–${t(c.end)}`}>
                            {t(c.start)}–{t(c.end)}
                          </button>
                        ))}
                      </div>
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right text-slate-500 tabular-nums">{r.hours ? r.hours.toFixed(1) : '—'}</td>
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
